import { Scale, Trophy } from "lucide-react";

const METRICS = [
  { key: "score", label: "Health score", unit: "", max: 100, from: (f) => f.analysis?.soil_health?.score },
  { key: "ph", label: "pH", unit: "", max: 9, ideal: 6.5, from: (f) => f.soil?.ph },
  { key: "moisture", label: "Moisture", unit: "%", max: 100, from: (f) => f.soil?.moisture },
  { key: "organic_matter", label: "Organic matter", unit: "%", max: 15, from: (f) => f.soil?.organic_matter },
  { key: "nitrogen", label: "Nitrogen", unit: "/100", max: 100, from: (f) => f.soil?.nitrogen },
  { key: "phosphorus", label: "Phosphorus", unit: "/100", max: 100, from: (f) => f.soil?.phosphorus },
  { key: "potassium", label: "Potassium", unit: "/100", max: 100, from: (f) => f.soil?.potassium },
];

const better = (m, a, b) => {
  if (a == null || b == null || a === b) return null;
  if (m.ideal != null) return Math.abs(a - m.ideal) < Math.abs(b - m.ideal) ? "a" : "b";
  return a > b ? "a" : "b";
};

function Bar({ value, max, color }) {
  return <div className="h-2 rounded-full bg-stone-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, ((value || 0) / max) * 100)}%`, backgroundColor: color }} /></div>;
}

export function FieldCompare({ a, b }) {
  if (!a || !b) return null;
  const wins = METRICS.reduce((acc, m) => { const w = better(m, m.from(a), m.from(b)); if (w) acc[w]++; return acc; }, { a: 0, b: 0 });
  const overall = wins.a === wins.b ? null : wins.a > wins.b ? a : b;

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6" data-testid="field-compare">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 mb-6">
        <div className="text-right"><div className="font-display font-bold text-emerald-950 truncate" data-testid="compare-name-a">{a.name}</div><div className="text-xs text-stone-500">{a.soil?.soil_type} · {a.location || "—"}</div></div>
        <Scale className="h-6 w-6 text-stone-400" />
        <div><div className="font-display font-bold text-emerald-950 truncate" data-testid="compare-name-b">{b.name}</div><div className="text-xs text-stone-500">{b.soil?.soil_type} · {b.location || "—"}</div></div>
      </div>

      <div className="space-y-4">
        {METRICS.map((m) => {
          const va = m.from(a), vb = m.from(b), w = better(m, va, vb);
          return (
            <div key={m.key} data-testid={`compare-metric-${m.key}`} className="grid grid-cols-[1fr_120px_1fr] items-center gap-3">
              <div>
                <div className={`text-right font-mono-tech text-sm font-bold ${w === "a" ? "text-[#2D6A4F]" : "text-stone-600"}`}>{va ?? "—"}{m.unit}{w === "a" && " ★"}</div>
                <Bar value={va} max={m.max} color={w === "a" ? "#2D6A4F" : "#A8A29E"} />
              </div>
              <div className="text-center text-xs uppercase tracking-wider text-stone-500 font-semibold">{m.label}</div>
              <div>
                <div className={`font-mono-tech text-sm font-bold ${w === "b" ? "text-[#2D6A4F]" : "text-stone-600"}`}>{w === "b" && "★ "}{vb ?? "—"}{m.unit}</div>
                <Bar value={vb} max={m.max} color={w === "b" ? "#2D6A4F" : "#A8A29E"} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-stone-100">
        {[a, b].map((f, i) => (
          <div key={i}>
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Top crops · {f.name}</div>
            <div className="flex flex-wrap gap-1.5">
              {(f.analysis?.crops || []).slice(0, 4).map((c, j) => (
                <span key={j} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#EAF6EF] text-[#2D6A4F]">{c.name} {Math.round(c.match || 0)}%</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div data-testid="compare-verdict" className="mt-6 rounded-xl bg-[#143D29] text-emerald-50 p-4 flex items-center gap-3">
        <Trophy className="h-5 w-5 text-[#52B788] shrink-0" />
        <p className="text-sm">
          {overall ? <><span className="font-bold">{overall.name}</span> is the healthier ground — it wins {Math.max(wins.a, wins.b)} of {METRICS.length} measures.</> : "Both fields are evenly matched across the measured values."}
        </p>
      </div>
    </div>
  );
}
